import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Tooltip } from "antd";
import dayjs from "dayjs";
import { DeleteOutlined } from "@ant-design/icons";
import CTable from "components/CTable";
import useFilter from "components/Filter";
import {
  getGridify,
  getStatus,
  showConfirm,
  showError,
  useNewState,
} from "helpers/utility";
import useAjax from "helpers/ajax";
import Toolbar from "components/Toolbar";
import { DEFAULT_PAGE_SIZE } from "constants";
import { filterTypes } from "components/Filter/constants";
import { useAuth } from "helpers/auth";
import CouponTransfer from "./transfer";
import Paragraph from "antd/lib/typography/Paragraph";

const Coupons = () => {
  const { get, del } = useAjax();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useNewState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const {
    user: { scopes },
  } = useAuth();

  const onDelete = (record) => {
    showConfirm("Data once deleted can not be restored.").then(() => {
      del("api/v1/coupons", { keys: [record.renewCouponID] })
        .then(() => {
          const pager = { ...pagination, current: 1 };
          loadData(pager);
        })
        .catch((err) => {
          showError(err);
        });
    });
  };

  const onDeleteAll = () => {
    showConfirm("Data once deleted can not be restored.").then(() => {
      del("api/v1/coupons", { keys: checkedKeys })
        .then(() => {
          const pager = { ...pagination, current: 1 };
          loadData(pager);
        })
        .catch((err) => {
          showError(err);
        });
    });
  };

  const columns = [
    {
      title: "Actions",
      width: "65px",
      align: "center",
      render: (text, record) => (
        <Button.Group>
          <Tooltip placement="topLeft" title="Delete">
            <Button
              type="danger"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record)}
              disabled={
                record.authorizationKey !== null &&
                record.authorizationKey !== ""
              }
            />
          </Tooltip>
        </Button.Group>
      ),
    },
    {
      title: "Distributor name",
      dataIndex: "companyName",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Coupon key",
      dataIndex: "couponKey",
      sorter: true,
      ...useFilter(),
      render: (text) => <Paragraph copyable>{text}</Paragraph>,
    },
    {
      title: "Days",
      dataIndex: "noOfDays",
      sorter: true,
      ...useFilter({ type: filterTypes.NUMBER }),
    },
    {
      title: "Authorization key",
      dataIndex: "authorizationKey",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Expiry date",
      dataIndex: "expiryDate",
      sorter: true,
      ...useFilter({ type: filterTypes.DATE }),
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Activated by",
      dataIndex: "activatedByName",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Activated on",
      dataIndex: "activationDate",
      sorter: true,
      ...useFilter(),
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Created by",
      dataIndex: "createdByName",
      sorter: true,
    },
    {
      title: "Created date",
      dataIndex: "createdOn",
      sorter: true,
      ...useFilter({ type: filterTypes.DATE }),
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Status",
      dataIndex: "active",
      width: "100px",
      sorter: true,
      ...useFilter({ type: filterTypes.YESNO }),
      render: (text) => getStatus(text),
    },
  ];

  const loadData = (pager, filters, sorter) => {
    const gridify = getGridify(pager, filters, sorter, [
      {
        field: "expiryRank",
        order: "ascend",
      },
      {
        field: "createdOn",
        order: "descend",
      },
    ]);
    get("api/v1/coupons", { ...gridify })
      .then((resp) => {
        setData(resp.data);
        setPagination({
          ...pager,
          total: resp.count,
        });
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => setLoading(false));
  };

  const onTableChange = (pager, filters, sorter) => {
    loadData(pager, filters, sorter);
  };

  useEffect(() => {
    loadData(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setCheckedKeys(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled:
        record.authorizationKey !== null && record.authorizationKey !== "",
    }),
  };

  return (
    <div>
      <Toolbar>
        {scopes.includes("coupons.create") && (
          <Button
            type="primary"
            onClick={() => {
              navigate("/devices/coupons/create");
            }}
          >
            Create
          </Button>
        )}
        <Button
          type="primary"
          danger
          disabled={checkedKeys.length <= 0}
          onClick={() => {
            onDeleteAll();
          }}
        >
          Delete all
        </Button>
        <Button
          type="primary"
          disabled={checkedKeys.length <= 0}
          onClick={() => {
            setVisible(true);
          }}
        >
          Transfer
        </Button>
      </Toolbar>
      <CTable
        rowKey="renewCouponID"
        columns={columns}
        dataSource={data}
        rowSelection={rowSelection}
        pagination={pagination}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
      />
      {visible && (
        <CouponTransfer
          title={`Transferring ${checkedKeys.length} coupons`}
          visible={visible}
          keys={checkedKeys}
          onClose={(success) => {
            setVisible(false);
            if (success) {
              loadData(pagination);
            }
          }}
        />
      )}
    </div>
  );
};
export default Coupons;
